import { ClassifierOption } from '@dealstack/frontend/app/pages/Valuations/shared/entityClassiferBadgeConfig';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import { ValuationSummaryGroupByPersonRes } from '@dealstack/shared/lib/generated/models/ValuationSummaryGroupByPersonRes';
import { reduce } from 'lodash';
import { NewByPersonValuationRow } from './ByPersonValuationTable.utils.types';

interface Aggregation {
	proceeds: HighPrecisionDecimal;
	securitiesHeld: HighPrecisionDecimal;
	receivables: HighPrecisionDecimal;
	cashOnBalanceSheet: HighPrecisionDecimal;
	outflows: HighPrecisionDecimal;
	securitiesIssued: HighPrecisionDecimal;
	payables: HighPrecisionDecimal;
	netProceeds: HighPrecisionDecimal;
	moneyIn: HighPrecisionDecimal | undefined;
	moneyOut: HighPrecisionDecimal | undefined;
	totalNetProceedsPercent: HighPrecisionDecimal | undefined;
}

export function toByPersonTableData(
	summaryData: ValuationSummaryGroupByPersonRes
): NewByPersonValuationRow[] {
	const totalNetProceeds = reduce(
		summaryData.persons
			.flatMap((person) => person.groups)
			.filter((group) => new HighPrecisionDecimal(group.retained.total).gt(0)),
		(acc, group) => {
			return acc.add(group.retained.total);
		},
		new HighPrecisionDecimal(0)
	);

	return summaryData.persons.map((person) => {
		const groupRows: NewByPersonValuationRow[] = person.groups.map((group) => {
			const netProceeds = new HighPrecisionDecimal(group.retained.total);

			let percentageOfTotalNetProceeds: HighPrecisionDecimal | undefined;
			if (netProceeds.gte(0) && !totalNetProceeds.isZero()) {
				percentageOfTotalNetProceeds = netProceeds.div(totalNetProceeds).mul(100);
			} else if (totalNetProceeds.isZero()) {
				percentageOfTotalNetProceeds = new HighPrecisionDecimal(0);
			}

			const classifiers: ClassifierOption[] = [
				...(group.id && group.name
					? [
							{
								label: group.name,
								value: group.id,
							},
						]
					: []),
				...person.classifiers.map((tag) => ({
					label: tag,
					value: tag,
				})),
			];

			return {
				person: person.displayName,
				proceeds: new HighPrecisionDecimal(group.proceeds?.total ?? '0'),
				securitiesHeld: new HighPrecisionDecimal(group.proceeds?.securities ?? '0'),
				receivables: new HighPrecisionDecimal(group.proceeds?.receivables ?? '0'),
				cashOnBalanceSheet: new HighPrecisionDecimal(group.proceeds?.cash ?? '0'),
				outflows: new HighPrecisionDecimal(group.outflows?.total ?? '0'),
				securitiesIssued: new HighPrecisionDecimal(group.outflows?.securities ?? '0'),
				payables: new HighPrecisionDecimal(group.outflows?.payables ?? '0'),
				netProceeds,
				totalNetProceedsPercent: percentageOfTotalNetProceeds,
				moneyIn: group.moneyIn ? new HighPrecisionDecimal(group.moneyIn) : undefined,
				moneyOut: group.moneyOut ? new HighPrecisionDecimal(group.moneyOut) : undefined,
				moic: group.moic ? new HighPrecisionDecimal(group.moic) : undefined,
				xirr: group.xirr ? new HighPrecisionDecimal(group.xirr) : undefined,
				metadata: {
					type: 'group',
					depth: 1,
					classifiers,
					securitiesBreakdown: group.proceeds?.securitiesBreakdown,
					outflowsSecuritiesBreakdown: group.outflows?.securitiesBreakdown,
					totalNetProceeds,
				},
			};
		});

		// Single group: return a flat row (no sub-rows)
		if (groupRows.length === 1) {
			return {
				...groupRows[0],
				person: person.displayName,
				metadata: {
					type: 'person',
					classifiers: groupRows[0].metadata?.classifiers,
					securitiesBreakdown: groupRows[0].metadata?.securitiesBreakdown,
					outflowsSecuritiesBreakdown: groupRows[0].metadata?.outflowsSecuritiesBreakdown,
					totalNetProceeds,
				},
			};
		}

		// Multiple groups: create a parent row with sub-rows

		const initial: Aggregation = {
			proceeds: new HighPrecisionDecimal(0),
			securitiesHeld: new HighPrecisionDecimal(0),
			receivables: new HighPrecisionDecimal(0),
			cashOnBalanceSheet: new HighPrecisionDecimal(0),
			outflows: new HighPrecisionDecimal(0),
			securitiesIssued: new HighPrecisionDecimal(0),
			payables: new HighPrecisionDecimal(0),
			netProceeds: new HighPrecisionDecimal(0),
			moneyIn: undefined,
			moneyOut: undefined,
			totalNetProceedsPercent: undefined,
		};

		const aggregated = groupRows.reduce<Aggregation>(
			(acc, row) => ({
				proceeds: acc.proceeds.add(row.proceeds),
				securitiesHeld: acc.securitiesHeld.add(row.securitiesHeld),
				receivables: acc.receivables.add(row.receivables),
				cashOnBalanceSheet: acc.cashOnBalanceSheet.add(row.cashOnBalanceSheet),
				outflows: acc.outflows.add(row.outflows),
				securitiesIssued: acc.securitiesIssued.add(row.securitiesIssued),
				payables: acc.payables.add(row.payables),
				netProceeds: acc.netProceeds.add(row.netProceeds),
				moneyIn: row.moneyIn
					? (acc.moneyIn ?? new HighPrecisionDecimal(0)).add(row.moneyIn)
					: acc.moneyIn,
				moneyOut: row.moneyOut
					? (acc.moneyOut ?? new HighPrecisionDecimal(0)).add(row.moneyOut)
					: acc.moneyOut,
				totalNetProceedsPercent: row.totalNetProceedsPercent
					? (acc.totalNetProceedsPercent ?? new HighPrecisionDecimal(0)).add(
							row.totalNetProceedsPercent
						)
					: acc.totalNetProceedsPercent,
			}),
			initial
		);

		const groupClassifiers: ClassifierOption[] = person.groups
			.filter((group) => group.id && group.name)
			.map((group) => ({ label: group.name!, value: group.id! }));

		const personClassifiers: ClassifierOption[] = person.classifiers.map((tag) => ({
			label: tag,
			value: tag,
		}));

		return {
			person: person.displayName,
			...aggregated,
			subRows: groupRows,
			metadata: {
				type: 'person',
				classifiers: [...groupClassifiers, ...personClassifiers],
				totalNetProceeds,
			},
		};
	});
}
